/**
 * Account Settings Page - YUL ON TAP
 * 
 * User self-service account management:
 * - Password change (triggers session invalidation)
 * - Account information display
 * - Theme preferences
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import CommandBar from '../components/CommandBar';
import { toast } from 'sonner';
import {
  User,
  Lock,
  Loader2,
  Shield,
  CheckCircle2,
  Moon,
  Sun,
  Palette,
} from 'lucide-react';
import { tLang } from '../lib/langHelper';

export default function AccountSettings() {
  const { t, i18n } = useTranslation();
  const { user, logout, currentWorkspace, getWorkspaceRoute } = useAuth();
  const { theme, setTheme, isManualPreference, clearPreference, checkTimeBasedPrompt } = useTheme();
  const navigate = useNavigate();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  useEffect(() => {
    // Check for time-based dark mode prompt
    checkTimeBasedPrompt();
  }, [checkTimeBasedPrompt]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (newPassword !== confirmPassword) {
      toast.error(tLang(i18n.language, 'Passwords do not match', 'Les mots de passe ne correspondent pas', 'Passwords do not match'));
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error(tLang(i18n.language, 'Password must be at least 8 characters', 'Le mot de passe doit contenir au moins 8 caractères', 'Password must be at least 8 characters'));
      return;
    }
    
    setIsLoading(true);
    
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setPasswordChanged(true);
      toast.success(tLang(i18n.language, 'Password changed. Please login again.', 'Mot de passe modifié. Veuillez vous reconnecter.', 'Password changed. Please login again.'));
      
      // Logout after 2 seconds
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
      
    } catch (error) {
      const message = error.response?.data?.detail || 
        (tLang(i18n.language, 'Error changing password', 'Erreur lors du changement de mot de passe', 'Error changing password'));
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (passwordChanged) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="bg-card border-border w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">
                {tLang(i18n.language, 'Password Changed!', 'Mot de passe modifié!', '¡Contraseña cambiada!')}
              </h2>
              <p className="text-muted-foreground">
                {tLang(i18n.language, 'Redirecting to login page...', 'Redirection vers la page de connexion...', 'Redirecting to login page...')}
              </p>
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Command Bar */}
      <CommandBar
        title={tLang(i18n.language, 'Account Settings', 'Paramètres du compte', 'Configuración de cuenta')}
        subtitle={user?.email}
      />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Account Info Card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center space-x-2">
              <User className="w-5 h-5" />
              <span>{tLang(i18n.language, 'Account Information', 'Informations du compte', 'Información de cuenta')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">
                  {tLang(i18n.language, 'Name', 'Nom', 'Nombre')}
                </Label>
                <p className="text-foreground font-medium">{user?.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">
                  {tLang(i18n.language, 'Email', 'Email', 'Correo')}
                </Label>
                <p className="text-foreground font-medium">{user?.email}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">
                  {tLang(i18n.language, 'Role', 'Rôle', 'Rol')}
                </Label>
                <p className="text-foreground font-medium capitalize">{user?.role}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">
                  {tLang(i18n.language, 'Industry', 'Secteur', 'Industria')}
                </Label>
                <p className="text-foreground font-medium capitalize">{user?.industry_type}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appearance Settings Card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center space-x-2">
              <Palette className="w-5 h-5" />
              <span>{tLang(i18n.language, 'Appearance', 'Apparence', 'Apariencia')}</span>
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {tLang(i18n.language,
                'Customize the look and feel of the interface',
                "Personnalisez l'apparence de l'interface",
                'Personalice la apariencia de la interfaz')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-foreground font-medium">
                    {tLang(i18n.language, 'Theme', 'Thème', 'Tema')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {tLang(i18n.language,
                      isManualPreference ? 'Manual preference active' : 'Using default settings',
                      isManualPreference ? 'Préférence manuelle active' : 'Utilise les paramètres par défaut',
                      isManualPreference ? 'Preferencia manual activa' : 'Usando configuración predeterminada')}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant={theme === 'light' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('light')}
                    data-testid="theme-light-btn"
                    className={theme === 'light' ? 'bg-primary text-primary-foreground' : ''}
                  >
                    <Sun className="w-4 h-4 mr-2" />
                    {tLang(i18n.language, 'Light', 'Clair', 'Claro')}
                  </Button>
                  <Button
                    variant={theme === 'dark' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('dark')}
                    data-testid="theme-dark-btn"
                    className={theme === 'dark' ? 'bg-primary text-primary-foreground' : ''}
                  >
                    <Moon className="w-4 h-4 mr-2" />
                    {tLang(i18n.language, 'Dark', 'Sombre', 'Oscuro')}
                  </Button>
                </div>
              </div>
              
              {isManualPreference && (
                <div className="pt-2 border-t border-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearPreference}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {tLang(i18n.language, 'Reset to default settings', 'Réinitialiser aux paramètres par défaut', 'Reset to default settings')}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center space-x-2">
              <Lock className="w-5 h-5" />
              <span>{tLang(i18n.language, 'Change Password', 'Changer le mot de passe', 'Cambiar contraseña')}</span>
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {tLang(i18n.language, 'After changing your password, you will need to login again on all your devices.', 'Après avoir changé votre mot de passe, vous devrez vous reconnecter sur tous vos appareils.', 'After changing your password, you will need to login again on all your devices.')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword" className="text-foreground">
                  {tLang(i18n.language, 'Current Password', 'Mot de passe actuel', 'Contraseña actual')}
                </Label>
                <Input
                  id="currentPassword"
                  data-testid="current-password-input"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="bg-background border-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-foreground">
                  {tLang(i18n.language, 'New Password', 'Nouveau mot de passe', 'Nueva contraseña')}
                </Label>
                <Input
                  id="newPassword"
                  data-testid="new-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="bg-background border-input"
                />
                <p className="text-xs text-muted-foreground">
                  {tLang(i18n.language, 'Minimum 8 characters', 'Minimum 8 caractères', 'Mínimo 8 caracteres')}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-foreground">
                  {tLang(i18n.language, 'Confirm Password', 'Confirmer le mot de passe', 'Confirmar contraseña')}
                </Label>
                <Input
                  id="confirmPassword"
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-background border-input"
                />
              </div>
              
              {/* Security Notice */}
              <div className="flex items-start space-x-3 p-3 bg-[hsl(var(--warning))]/10 border border-[hsl(var(--warning))]/30 rounded-lg">
                <Shield className="w-5 h-5 text-[hsl(var(--warning))] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[hsl(var(--warning))]">
                  {tLang(i18n.language, 'Changing your password will sign out all currently logged-in devices.', 'Changer votre mot de passe déconnectera tous les appareils actuellement connectés.', 'Changing your password will sign out all currently logged-in devices.')}
                </p>
              </div>
              
              <Button
                type="submit"
                data-testid="change-password-btn"
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {tLang(i18n.language, 'Changing...', 'Modification...', 'Cambiando...')}
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    {tLang(i18n.language, 'Change Password', 'Changer le mot de passe', 'Cambiar contraseña')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
